
import { defineComponent, ref } from "vue";
import router from "@/router";
import store from "@/store";
import { useRoute } from "vue-router";
import TeacherService from "@/api/Aula/TeachersService";
import { Tab } from "bootstrap"; // top of file
import JourneysService from "@/api/Configuration/JourneysService";
import HeadquartersService from "@/api/Configuration/HeadquartersService";
import type { IJourneysModel, IHeadquartersModel } from "@/interfaces/modules";
import PersonelInformationComponent from "./TabPersonalInformation.vue";
import TabAcademicComponent from "./TabAcademic.vue";
import TabHealthComponent from "./TabHealth.vue";
import TabContractComponent from "./TabContract.vue";
import TabEmergencyComponent from "./TabEmergency.vue";

export default defineComponent({
  name: "StudentTab",
  components: {
    PersonelInformationComponent,
    TabAcademicComponent,
    TabHealthComponent,
    TabContractComponent,
    TabEmergencyComponent,
  },
  data() {
    const route = useRoute();

    return {
      wasValidated: false,          // <— drives invalid styles in children
      teacherId: route.params.Id || 0,
      file: null as File | null,
      previewUrl: new URL('@/assets/images/user/sin_imagen.png', import.meta.url).href, // default preview
      objectUrl: undefined as string | undefined,
      form: {
        loading: true,
        record: {} as any
      },
      Journeys: ref<IJourneysModel[]>([]),
      Headquarters: ref<IHeadquartersModel[]>([])
    };
  },
  async mounted() {
    if (parseInt(this.teacherId as any) > 0) {
      this.form.record = await TeacherService.GetTeachersbyId({ Id: this.teacherId });
      if (this.form.record.BirthDate) {
        this.form.record.BirthDate = this.form.record.BirthDate.substring(0, 10);
      }
      if (this.form.record.DateUndergrade) {
        this.form.record.DateUndergrade = this.form.record.DateUndergrade.substring(0, 10);
      }
      if (this.form.record.DatePostgraduate) {
        this.form.record.DatePostgraduate = this.form.record.DatePostgraduate.substring(0, 10);
      }
      if (this.form.record.DateFirstActPossession) {
        this.form.record.DateFirstActPossession = this.form.record.DateFirstActPossession.substring(0, 10);
      }
      if (this.form.record.DateLastActPossession) {
        this.form.record.DateLastActPossession = this.form.record.DateLastActPossession.substring(0, 10);
      }
      if (this.form.record.ResolutionDate) {
        this.form.record.ResolutionDate = this.form.record.ResolutionDate.substring(0, 10);
      }
      if (this.form.record.Image)
        this.previewUrl = this.form.record.Image
    }
    this.Journeys = await JourneysService.GetJourneys();
    this.Headquarters = await HeadquartersService.GetHeadquarters();
  },
  methods: {
    cancel() { router.push({ name: "Students" }); },

    GetHeadquarter(): string {
      const id = this.form?.record?.HeadquarterId;
      if (!id || !Array.isArray(this.Headquarters)) return "";
      const hq = this.Headquarters.find(h => h.Id === id);
      return hq?.Description ?? "";
    },

    GetJourney(): string {
      const id = this.form?.record?.JourneyId;
      if (!id || !Array.isArray(this.Journeys)) return "";
      const j = this.Journeys.find(x => x.Id === id);
      return j?.Name ?? "";
    },

    handleFileChange(e: Event) {
      const input = e.target as HTMLInputElement;
      const f = input.files?.[0] ?? null;

      // cleanup previous URL
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = undefined;
      }

      this.file = f;
      if (f) {
        this.objectUrl = URL.createObjectURL(f);
        this.previewUrl = this.objectUrl;        // <— string for <img :src>
      } else {
        // fallback or from record if you have it
        this.previewUrl = new URL('@/assets/images/user/sin_imagen.png', import.meta.url).href;
      }
    },
    async handleSubmit(e: Event) {
      const formComp = this.$refs.formRecord as any;
      const formEl: HTMLFormElement = formComp?.$el ?? formComp;
      this.wasValidated = true; // show invalid styling

      // 1) Native HTML5 validation for all tabs
      if (!formEl.checkValidity()) {
        e.preventDefault();
        e.stopPropagation();
        this.jumpToFirstInvalid(formEl);
        store.dispatch("core/popupDialog", {
          text: "Faltan campos obligatorios. Revise las pestañas marcadas.",
          type: "warning",
          showCancelButton: false,
        });
        return;
      }

      // 2) Optional: custom cross-tab rules exposed by children
      const childRefs = [
        this.$refs.personRef,
        this.$refs.academicRef,
        this.$refs.healthRef,
        this.$refs.contractRef,
        this.$refs.emergencyRef
      ].filter(Boolean) as any[];

      for (const r of childRefs) {
        if (typeof r?.validate === "function") {
          const res = await r.validate(this.form.record);
          if (!res.ok) {
            this.jumpToPaneId(res.paneId);  // make your child return its paneId
            store.dispatch("core/popupDialog", {
              text: res.message || "Hay errores de validación.",
              type: "warning",
              showCancelButton: false,
            });
            return;
          }
        }
      }

      // 3) If all good, submit
      try {
        // Files
        const pictureFile: File | null = this.file || null;

        // CSV comes from the child via ref
        const academicRef = this.$refs.academicRef as { getCvFile?: () => File | null } | undefined;
        const csvFile: File | null = academicRef?.getCvFile?.() || null;

        // Build multipart
        const formData = new FormData();

        // JSON payload
        formData.append(
          "teacher",
          new Blob([JSON.stringify(this.form.record)], { type: "application/json" })
        );

        // Optional: picture
        if (pictureFile) {
          formData.append("picture", pictureFile, pictureFile.name || "picture");
        }

        // Optional: CSV from child
        if (csvFile) {
          // change "cv" to the exact field name your API expects
          formData.append("cv", csvFile, csvFile.name || "cv.csv");
        }

        // Send
        const res = await TeacherService.PostTeachers(formData);
        store.dispatch("core/popupDialog", {
          text: "Registro completado con éxito.",
          type: "success",
          showCancelButton: false,
        });
        this.form.record = res;
        router.push({ name: "TeachersDetails", params: { id: this.form.record.Id } });
        location.reload();

      } catch (err: any) {
        store.dispatch("core/popupDialog", {
          text: `Error: ${err?.message || "No se pudo guardar"}`,
          type: "error",
          showCancelButton: false,
        });
      } finally {
        this.form.loading = false;
      }
    },

    jumpToFirstInvalid(formEl: HTMLFormElement) {
      // Picks the first invalid control anywhere in the form (hidden or not)
      const firstInvalid = formEl.querySelector<HTMLElement>(":invalid");
      if (!firstInvalid) return;

      // Find its tab pane & show it
      const pane = firstInvalid.closest<HTMLElement>(".tab-pane");
      if (pane?.id) this.jumpToPaneId(pane.id);

      // Focus + show native tooltip + add a quick visual highlight
      requestAnimationFrame(() => {
        // put caret/focus where the error is
        firstInvalid.focus({ preventScroll: true });
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });

        // Ask the browser to display the built-in validation bubble (Chrome/Edge/Firefox)
        // This makes the message like “Please fill out this field” appear right on the control
        if ("reportValidity" in firstInvalid) {
          (firstInvalid as any).reportValidity?.();
        }

        // Add a temporary highlight (nice glow) so the user sees it instantly
        this.flashInvalid(firstInvalid);
      });
    },

    jumpToPaneId(paneId: string) {
      const trigger = document.querySelector<HTMLElement>(`[data-bs-target="#${paneId}"]`);
      if (!trigger) return;
      const tab = new Tab(trigger);
      tab.show();
    },

    flashInvalid(el: HTMLElement) {
      // Add class to the control or its .form-control wrapper
      const target =
        el.classList.contains("form-control") || el.classList.contains("form-select")
          ? el
          : (el.querySelector(".form-control, .form-select") as HTMLElement) || el;

      target.classList.add("field-flash");
      setTimeout(() => target.classList.remove("field-flash"), 1200);
    }
  }
});
